import React, { useState, useCallback } from 'react'
import { Button, Space, Tooltip } from 'antd'
import { LockFilled } from '@ant-design/icons'
import { GridApi } from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import { useDiscardChangesModal } from '../../modals'

interface EditModeActionsProps<T = any> {
  /**
   * AG Grid API instance
   */
  gridApi?: GridApi<T>

  /**
   * Number of rows with unsaved changes, or a function that returns the count
   * Using a function ensures the count is always up-to-date when checked
   */
  changedRowsCount?: number | (() => number)

  /**
   * Whether the grid is in edit mode (controlled)
   */
  isEditMode: boolean

  /**
   * Callback when entering edit mode
   */
  onEnterEditMode: () => void

  /**
   * Callback when exiting edit mode
   */
  onExitEditMode: () => void

  /**
   * Callback to discard changes
   */
  onDiscard: () => void

  /**
   * Optional callback when save is triggered
   */
  onSave?: () => void | Promise<void>

  /**
   * Make Edit button primary style (use when editing is the main action)
   * @default false
   */
  primaryEdit?: boolean

  /**
   * Disable the edit button (e.g. when the budgeting scenario is locked)
   * @default false
   */
  disabled?: boolean

  /**
   * Tooltip shown when the button is disabled
   */
  disabledTooltip?: string
}

/**
 * Edit mode actions component for GridToolbar
 * Manages edit mode state and updates grid context dynamically
 *
 * @example
 * ```tsx
 * const { hasUnsavedChanges, changedRowsCount, discardChanges } = useGridDataActions({ gridApi })
 *
 * <GridToolbar>
 *   <GridToolbar.Actions align="space-between">
 *     <div>
 *       {hasUnsavedChanges() && <GridToolbar.UnsavedChanges count={changedRowsCount()} />}
 *     </div>
 *     <GridToolbar.EditModeActions
 *       gridApi={gridApi}
 *       changedRowsCount={changedRowsCount}  // Pass function for always up-to-date count
 *       onDiscard={discardChanges}
 *       onSave={handleSave}
 *       primaryEdit={true}  // Use when editing is the main page action
 *     />
 *   </GridToolbar.Actions>
 * </GridToolbar>
 * ```
 */
export const EditModeActions = ({
  gridApi,
  changedRowsCount = 0,
  isEditMode,
  onEnterEditMode,
  onExitEditMode,
  onDiscard,
  onSave,
  primaryEdit = false,
  disabled = false,
  disabledTooltip
}: EditModeActionsProps) => {
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const discardChangesModal = useDiscardChangesModal()

  const handleSave = useCallback(async () => {
    if (!onSave) {
      onExitEditMode()
      return
    }

    setIsSaving(true)
    try {
      await onSave()
      // onSave is responsible for calling onExitEditMode when appropriate
    } catch (error) {
      console.error('Save failed:', error)
      // Don't exit edit mode on error
    } finally {
      setIsSaving(false)
    }
  }, [onSave, onExitEditMode])

  const handleDiscard = useCallback(() => {
    // Get current count (supports both number and function)
    const count = typeof changedRowsCount === 'function' ? changedRowsCount() : changedRowsCount || 0

    // If there are no changes, discard immediately without confirmation
    if (count === 0) {
      onDiscard()
      onExitEditMode()
      return
    }

    // Show confirmation modal if there are unsaved changes
    discardChangesModal({
      onConfirm: () => {
        onDiscard()
        onExitEditMode()
      }
    })
  }, [changedRowsCount, onDiscard, onExitEditMode])

  if (!isEditMode) {
    return (
      <Tooltip title={disabled ? disabledTooltip : undefined}>
        <Button
          style={{
            minWidth: 75
          }}
          type={primaryEdit ? 'primary' : 'default'}
          onClick={onEnterEditMode}
          disabled={!gridApi || disabled}
          icon={disabled ? <LockFilled /> : undefined}
        >
          {t('global:edit', 'Edit')}
        </Button>
      </Tooltip>
    )
  }

  return (
    <Space>
      <Button
        style={{
          minWidth: 75
        }}
        onClick={handleDiscard}
        disabled={isSaving}
      >
        {t('global:cancel', 'Peruuta')}
      </Button>
      <Button
        style={{
          minWidth: 75
        }}
        type="primary"
        onClick={handleSave}
        loading={isSaving}
      >
        {t('global:save', 'Save')}
      </Button>
    </Space>
  )
}

export default EditModeActions
